import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ActiveReceivingPage.css';
import ActiveReceivingHeader from './components/ActiveReceivingHeader';
import ReceivingTradeCard from './components/ReceivingTradeCard';
import FirstTradeCelebrationModal from './components/FirstTradeCelebrationModal';
import { useGetActiveTradesQuery } from 'store/services/trade/tradeApi';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';
import SearchInput from 'components/SearchInput/SearchInput';
import Pagination from 'components/Pagination/Pagination';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { TRADES_PAGE_SIZE } from 'constants/trades';
import { useIsMobile } from 'hooks/useIsMobile';
import TradePanelSection from 'components/TradeTabPanel/TradePanelSection';
import TradeTabEmptyState from 'components/TradeTabPanel/TradeTabEmptyState';
import { PanelStatus } from 'components/TradeTabPanel/types';
import { useTradePanelState } from 'components/TradeTabPanel/useTradePanelState';

export type ReceivingStatus = 'preparing' | 'transit' | 'completed' | 'cancelled';

export interface ReceivingTradeItem {
  qty: number;
  name: string;
  set: string;
  setCode?: string;
  setIconUrl?: string;
  collectorNumber?: string;
  condition: string;
  language: string;
  image: string;
  scryfallId?: string;
  pointValue?: number;
  foil?: boolean;
  completedAt?: string | null;
}

export interface ReceivingTrade {
  id: string;
  tradeNumber: string;
  traderId: string;
  traderName: string;
  traderAvatar: string;
  traderIsElite?: boolean;
  traderShowAnimatedBorder?: boolean;
  traderBorderColor1?: string | null;
  traderBorderColor2?: string | null;
  pointBalance?: number;
  initiated: string;
  /** Start of the sender's 72h window (openedAt ?? initiatedAt); null while awaiting funding. */
  shipWindowStart: string | null;
  shipped?: string;
  completed?: string;
  cancelled?: string;
  estimatedDelivery?: string;
  status: ReceivingStatus;
  items: ReceivingTradeItem[];
  totalValue: number;
  pendingValue?: number;
  speed: number;
  accuracy: number;
  communication: number;
  shippingAddress: {
    name: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  cancelledRaw?: string;
  isRemovedItem?: boolean;
  removedFromTradeNumber?: string;
  // Trade-offer leg not yet open: it funds automatically when the viewer's
  // outbound funding trade completes and pays them. Only reservedPoints (the
  // viewer's net on the offer) has left their balance so far.
  awaitingFunding?: boolean;
  fundedByTradeNumber?: string;
  reservedPoints?: number;
}

export default function ActiveReceivingPage() {
  const [activeTab, setActiveTab] = useState<ReceivingStatus>('preparing');
  const [retryCount, setRetryCount] = useState(0);
  const [skipQuery, setSkipQuery] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const hasSetInitialTab = useRef(false);

  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  // When the field is cleared (X button), bypass the debounce so one click
  // restores the unfiltered list immediately.
  const effectiveSearch = searchTerm === '' ? '' : debouncedSearch;

  // New search restarts from page 1
  useEffect(() => {
    setPage(1);
  }, [effectiveSearch]);

  // Switching tabs keeps the search query but restarts from page 1
  const handleTabChange = (tab: ReceivingStatus) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // First trade celebration modal state (lifted here to survive trade card unmount)
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);
  const [celebrationShareImageUrl, setCelebrationShareImageUrl] = useState<string | null>(null);
  const [celebrationCards, setCelebrationCards] = useState<ReceivingTradeItem[]>([]);
  const celebrationTriggeredRef = useRef(false);

  const handleFirstTradeCompleted = useCallback((shareImageUrl: string | null, cards: ReceivingTradeItem[]) => {
    if (celebrationTriggeredRef.current) return; // Already triggered this session
    celebrationTriggeredRef.current = true;

    setCelebrationShareImageUrl(shareImageUrl);
    setCelebrationCards(cards);
    // Preload the share image during the delay so it may be cached by the time the modal opens
    if (shareImageUrl) {
      const img = new Image();
      img.src = shareImageUrl;
    }
    setTimeout(() => setShowCelebrationModal(true), 800);
  }, []);

  // Fetch one page of the active tab's trades from API.
  // The UI's "preparing" tab is the API's "ready" status.
  const apiStatus = activeTab === 'preparing' ? 'ready' : activeTab;
  const { data: tradesData, isLoading, isFetching, error, refetch } = useGetActiveTradesQuery(
    {
      type: 'receiving',
      status: apiStatus,
      page,
      limit: TRADES_PAGE_SIZE,
      search: effectiveSearch.trim() || undefined,
    },
    {
      // Skip the query if we've had too many 401 errors
      skip: skipQuery
    }
  );

  // Panel data is set-scoped (type + search), so it shares the list's debounced
  // search value: list, badges and panel can never disagree mid-typing.
  const panel = useTradePanelState('receiving', effectiveSearch, skipQuery);
  const isMobile = useIsMobile();

  // If the list shrinks under us (e.g. a trade completes and the last page
  // disappears after cache invalidation), fall back to the last valid page.
  useEffect(() => {
    if (tradesData && page > 1 && page > tradesData.pagination.totalPages) {
      setPage(Math.max(1, tradesData.pagination.totalPages));
    }
  }, [tradesData, page]);

  // Convert API trades to component format
  // Format date from ISO string to MM.DD.YYYY
  const formatDate = (dateString: string | undefined): string | undefined => {
    if (!dateString) return undefined;
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${month}.${day}.${year}`;
    } catch {
      return dateString;
    }
  };

  // Subscribe to real-time balance updates for visible traders
  const traderIds = useMemo(
    () => (tradesData?.trades || []).map(t => t.traderId),
    [tradesData?.trades]
  );
  useTraderBalanceSocket({ traderIds, enabled: traderIds.length > 0 });

  const trades: ReceivingTrade[] = tradesData?.trades.map(trade => ({
    id: trade.id,
    tradeNumber: trade.tradeNumber,
    traderId: trade.traderId.toString(),
    traderName: trade.traderName,
    traderAvatar: trade.traderAvatar || '',
    traderIsElite: trade.traderIsElite,
    traderShowAnimatedBorder: trade.traderShowAnimatedBorder,
    traderBorderColor1: trade.traderBorderColor1,
    traderBorderColor2: trade.traderBorderColor2,
    pointBalance: trade.pointBalance,
    initiated: formatDate(trade.initiated) || trade.initiated,
    shipWindowStart: trade.shipWindowStart,
    shipped: formatDate(trade.shipped) || trade.shipped,
    completed: formatDate(trade.completed) || trade.completed,
    cancelled: trade.cancelled ? (formatDate(trade.cancelled) || trade.cancelled) : undefined,
    cancelledRaw: trade.cancelled || undefined,
    estimatedDelivery: trade.estimatedDelivery,
    // Map statuses: ready -> preparing (sender hasn't shipped yet), cancelled stays
    // cancelled; pending_funding legs bucket into preparing with their waiting state
    status: trade.status === 'cancelled' ? 'cancelled' : (trade.status === 'ready' || trade.status === 'pending_funding') ? 'preparing' : trade.status === 'transit' ? 'transit' : 'completed',
    awaitingFunding: trade.awaitingFunding || trade.status === 'pending_funding',
    fundedByTradeNumber: trade.fundedByTradeNumber,
    reservedPoints: trade.reservedPoints ?? 0,
    items: trade.items.map(item => ({
      qty: item.qty,
      name: item.name,
      set: item.set,
      setCode: (item as any).setCode,
      setIconUrl: (item as any).setIconUrl,
      collectorNumber: (item as any).collectorNumber,
      condition: item.condition,
      language: item.language,
      image: item.image,
      scryfallId: (item as any).scryfallId,
      pointValue: item.pointValue || 0,
      foil: item.foil || false,
        finish: (item as any).finish ?? undefined,
      completedAt: item.completedAt,
    })),
    totalValue: trade.totalValue,
    pendingValue: trade.pendingValue,
    speed: trade.speed ?? 5,  // Rating from backend, default to 5
    accuracy: trade.accuracy ?? 5,  // Rating from backend, default to 5
    communication: trade.communication ?? 5,  // Rating from backend, default to 5
    shippingAddress: trade.shippingAddress ? {
      name: `${trade.shippingAddress.firstName || ''} ${trade.shippingAddress.lastName || ''}`.trim() || 'Not provided',
      street: trade.shippingAddress.address1 || '',
      city: trade.shippingAddress.city || '',
      state: trade.shippingAddress.state || '',
      zipCode: trade.shippingAddress.postalCode || '',
      country: trade.shippingAddress.country || '',
    } : {
      name: 'Not provided',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    isRemovedItem: (trade as any).isRemovedItem || false,
    removedFromTradeNumber: (trade as any).removedFromTradeNumber,
  })) || [];

  // The server returns the active tab's page already filtered, searched and
  // sorted (cancelled interleaves synthetic removed-item entries in SQL).

  // Auto-select the first tab with content on initial load. Reads statusCounts,
  // not byStatus: byStatus respects search, and a search must not bounce tabs.
  useEffect(() => {
    if (tradesData && !hasSetInitialTab.current) {
      hasSetInitialTab.current = true;
      if (tradesData.stats.statusCounts.ready === 0 && tradesData.stats.statusCounts.transit > 0) {
        setActiveTab('transit');
      }
    }
  }, [tradesData]);

  // Header block (with the per-tab panel) + tabs, shared by every render branch.
  // The shared components speak the API's status names; "preparing" is this
  // page's own name for "ready", mapped at this boundary as it is for the query.
  const panelSection = (
    <TradePanelSection
      direction="inbound"
      Header={ActiveReceivingHeader}
      activeStatus={apiStatus}
      onStatusChange={(status: PanelStatus) => handleTabChange(status === 'ready' ? 'preparing' : status)}
      stats={tradesData?.stats}
      panel={panel}
      searchActive={!!effectiveSearch.trim()}
    />
  );

  if (isLoading) {
    return (
      <div className="active-receiving-page">
        {panelSection}
        <SearchInput
          className="trades-search"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search for username or card name"
        />
        <div className="receiving-trades-container">
          <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
            Loading incoming trades...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorStatus = (error as any).status;
    const errorMessage = (error as any).data?.message || (error as any).data?.error;

    console.error('Failed to fetch receiving trades:', {
      status: errorStatus,
      message: errorMessage,
      fullError: error
    });

    // Special handling for 401 errors
    if (errorStatus === 401) {
      console.warn('⚠️ 401 error on /receiving page - This might be a backend permission issue');

      // Show a user-friendly error message instead of logging out
      return (
        <div className="active-receiving-page">
          {panelSection}
          <div className="receiving-trades-container">
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#fff',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h3 style={{ color: '#ff9800', marginBottom: '20px' }}>
                Unable to Load Incoming Trades
              </h3>
              <p style={{ marginBottom: '20px' }}>
                We're having trouble loading your incoming trades.
                Please try again in a moment.
              </p>
              <button
                onClick={() => {
                  const newCount = retryCount + 1;
                  setRetryCount(newCount);
                  if (newCount > 2) {
                    setSkipQuery(true);
                  }
                  refetch();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9c27b0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Try Again
              </button>
              <p style={{ marginTop: '20px', fontSize: '14px', color: '#aaa' }}>
                If this persists, try refreshing the page or logging out and back in.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // For other errors, show a generic error message
  }

  return (
    <div className="active-receiving-page">
      <h1 className="active-receiving-page-title">Active Trades</h1>
      {panelSection}
      <SearchInput
        className="trades-search"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search for username or card name"
      />
      <div
        className={`receiving-trades-container${isFetching ? ' trades-container-fetching' : ''}`}
        aria-busy={isFetching}
      >
        {trades.length > 0 ? (
          trades.map(trade => (
            <ReceivingTradeCard
              key={trade.id}
              trade={trade}
              onFirstTradeCompleted={handleFirstTradeCompleted}
            />
          ))
        ) : effectiveSearch.trim() ? (
          <div className="trades-search-empty">
            No trades match your search
          </div>
        ) : panel.collapsed ? (
          // Expanded, the panel itself carries the tab's empty state. Collapsed
          // there is no panel, so without this an empty tab would be blank.
          <TradeTabEmptyState
            direction="inbound"
            status={apiStatus}
            data={panel.data}
            isMobile={isMobile}
            placement="list"
          />
        ) : null}
        {tradesData && tradesData.pagination.totalPages > 1 && (
          <div className="trades-pagination">
            <Pagination
              currentPage={page}
              totalPages={tradesData.pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      <FirstTradeCelebrationModal
        isOpen={showCelebrationModal}
        onClose={() => setShowCelebrationModal(false)}
        shareImageUrl={celebrationShareImageUrl}
        receivedCards={celebrationCards}
      />
    </div>
  );
}